import { Phone } from "lucide-react";
import { cn } from "@/modern/utils";
import { swivuRecentRecordSortFn } from "@/modern/utils";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import type { Contact } from "@/bryntum-lib/models/Contact";
import { EntityFinderHighlight } from "@/modern/components/entity-finder/components/EntityFinderHighlight";
import { entityFinderRowClass } from "@/modern/components/entity-finder/components/EntityFinderItemRow";
import {
  canCreate,
  estimateDefaultRowHeight,
  openEntityEditor,
} from "@/modern/components/entity-finder/constants/config/shared";
import type { EntityFinderConfigEntry } from "@/modern/components/entity-finder/types";
import {
  ENTITY_FINDER_SUBTITLE_CLS,
  ENTITY_FINDER_TITLE_CLS,
} from "@/modern/components/entity-finder/constants/layout";
import {
  defaultEntityListSection,
  defaultMatchesScope,
  matchesSearch,
  passesArchiveFilter,
  searchModeActive,
} from "@/modern/components/entity-finder/utils";
import { UserAvatar, type BoardUser as AvatarUser } from "@/modern/components/user-avatar";

function mapContact(contact: Contact) {
  return {
    id: `contact-${contact.id}`,
    type: "contact" as const,
    label: contact.name || "N/A",
    subtitle: contact.email || undefined,
    record: contact,
  };
}

export const contactConfig: EntityFinderConfigEntry<"contact"> = {
  type: "contact",
  label: "Contact",
  plural: "Contacts",
  icon: Phone,
  tint: "bg-chart-5/15 text-chart-5",
  listOrder: 40,
  showInFilter: true,
  isActive: searchModeActive,
  matchesScope: (ctx) => defaultMatchesScope(ctx, "contact"),
  getData: (ctx) => {
    const searchLower = ctx.debouncedQuery.toLowerCase();
    const records = swivuRecentRecordSortFn(
      ctx.project.contactsStore.query(
        (contact: Contact) =>
          passesArchiveFilter(contact.archived, ctx.isShowArchive) &&
          !contact.isLinked &&
          (matchesSearch(contact.name, searchLower) || matchesSearch(contact.email, searchLower)),
        true
      ) as unknown as Contact[],
      {
        currentBoardUserId: ctx.currentBoardUserId,
        getUpdatedByField: (r) => (r as Contact).lastUpdatedBy,
        getUpdatedAtField: (r) => (r as Contact).lastUpdatedAt,
      }
    );
    return records.map(mapContact);
  },
  getRecentItem: (ctx) => {
    const records = swivuRecentRecordSortFn(
      ctx.project.contactsStore.query(
        (c: Contact) => passesArchiveFilter(c.archived, ctx.isShowArchive) && !c.isLinked,
        true
      ) as unknown as Contact[],
      {
        currentBoardUserId: ctx.currentBoardUserId,
        getUpdatedByField: (r) => (r as Contact).lastUpdatedBy,
        getUpdatedAtField: (r) => (r as Contact).lastUpdatedAt,
      }
    ).slice(0, 1);
    return records[0] ? mapContact(records[0]) : null;
  },
  buildListSection: (items, _ctx) => defaultEntityListSection(contactConfig, items),
  estimateRowHeight: estimateDefaultRowHeight,
  itemRenderer: ({ item, query }) => (
    <div className={entityFinderRowClass(Boolean(item.subtitle))}>
      <div className={cn("shrink-0", item.subtitle && "mt-0.5")}>
        <UserAvatar
          user={item.record as unknown as AvatarUser}
          size="default"
          showTooltip={false}
          className="shrink-0"
          avatarClassName="size-8"
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className={cn("truncate", ENTITY_FINDER_TITLE_CLS)}>
          <EntityFinderHighlight text={item.label} query={query} />
        </div>
        {item.subtitle ? (
          <p className={cn("mt-0.5 truncate", ENTITY_FINDER_SUBTITLE_CLS)}>
            <EntityFinderHighlight text={item.subtitle} query={query} tone="muted" />
          </p>
        ) : null}
      </div>
    </div>
  ),
  onSelect: (item, ctx) => {
    openEntityEditor(
      ctx,
      EDITOR_KEYS.contactEditor,
      { id: (item.record as Contact).id },
      { contact: item.record },
      { by: "recordId", useExisting: true }
    );
  },
  createOnUnmatched: {
    label: "Create new contact",
    fn: (query, ctx) => {
      const trimmed = query.trim();
      if (!trimmed) return;
      const newRecord = (
        ctx.project.contactsStore as { createRecord: (data?: object) => { id: string } }
      ).createRecord({ name: trimmed });
      openEntityEditor(
        ctx,
        EDITOR_KEYS.contactEditor,
        { id: newRecord.id },
        { contact: newRecord },
        { by: "recordType", useExisting: true }
      );
    },
    isEnabled: canCreate,
  },
};
